import React, { useEffect, useState } from "react";
import { HashRouter , Navigate, Route, Routes } from "react-router-dom";

import ProtectedRoute from "./components/shared/ProtectedRoute";
import Toast from "./components/shared/Toast";

// Auth
import LoginPage from "./features/auth/pages/LoginPage";
import RequestAccessPage from "./features/auth/pages/RequestAccessPage";

// Dashboard
import DashboardPage from "./features/dashboard/pages/DashboardPage";

// Notifications
import AccessRequestsPage from "./features/notifications/pages/AccessRequestsPage";
import RulesPage from "./features/notifications/pages/RulesPage";
import NotificationsPage from "./features/notifications/pages/NotificationsPage";

// Users & Classes
import UsersClassesHome from "./features/users-classes/pages/UsersClassesHome";
import UsersListPage from "./features/users-classes/pages/UsersListPage";
import UserDetailsPage from "./features/users-classes/pages/UserDetailsPage";
import ClassesListPage from "./features/users-classes/pages/ClassesListPage";
import ClassDetailsPage from "./features/users-classes/pages/ClassDetailsPage";

// Content
import ContentHome from "./features/content/pages/ContentHome";
import TipsListPage from "./features/content/pages/TipsListPage";
import TipDetailsPage from "./features/content/pages/TipDetailsPage";
import ChallengesListPage from "./features/content/pages/ChallengesListPage";
import ChallengeDetailsPage from "./features/content/pages/ChallengeDetailsPage";

// Stats
import StatsPage from "./features/stats/pages/StatsPage";

// Feedback
import FeedbackPage from "./features/feedback/pages/FeedbackPage";
import FeedbackReplyPage from "./features/feedback/pages/FeedbackReplyPage";

// Settings
import AccountSettingsPage from "./features/settings/pages/AccountSettingsPage";

function PrivateRoute({ element, allowedRoles }) {
  return <ProtectedRoute allowedRoles={allowedRoles}>{element}</ProtectedRoute>;
}

const STAFF_ROLES = ["admin", "teacher", "viewer"];
const ADMIN_ROLES = ["admin"];

export default function App() {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    window.showToast = (message, type = "success") => {
      setToast({ message: String(message || ""), type });
    };

    return () => {
      try {
        delete window.showToast;
      } catch {}
    };
  }, []);

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <HashRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/request-access" element={<RequestAccessPage />} />
          <Route path="/" element={<Navigate to="/login" replace />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute
                element={<DashboardPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />

          <Route
            path="/access-requests"
            element={
              <PrivateRoute
                element={<AccessRequestsPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />

          <Route
            path="/users-classes"
            element={
              <PrivateRoute
                element={<UsersClassesHome />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />
          <Route
            path="/users-classes/users"
            element={
              <PrivateRoute
                element={<UsersListPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />
          <Route
            path="/users-classes/users/:id"
            element={
              <PrivateRoute
                element={<UserDetailsPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />
          <Route
            path="/users-classes/classes"
            element={
              <PrivateRoute
                element={<ClassesListPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />
          <Route
            path="/users-classes/classes/:id"
            element={
              <PrivateRoute
                element={<ClassDetailsPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />

          <Route
            path="/content"
            element={
              <PrivateRoute
                element={<ContentHome />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/content/tips"
            element={
              <PrivateRoute
                element={<TipsListPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/content/tips/:id"
            element={
              <PrivateRoute
                element={<TipDetailsPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/content/challenges"
            element={
              <PrivateRoute
                element={<ChallengesListPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/content/challenges/:id"
            element={
              <PrivateRoute
                element={<ChallengeDetailsPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />

          <Route
            path="/stats"
            element={
              <PrivateRoute
                element={<StatsPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute
                element={<NotificationsPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/rules"
            element={
              <PrivateRoute
                element={<RulesPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/feedback"
            element={
              <PrivateRoute
                element={<FeedbackPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/feedback/:id"
            element={
              <PrivateRoute
                element={<FeedbackReplyPage />}
                allowedRoles={ADMIN_ROLES}
              />
            }
          />

          <Route
            path="/settings"
            element={
              <PrivateRoute
                element={<AccountSettingsPage />}
                allowedRoles={STAFF_ROLES}
              />
            }
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </HashRouter>
    </>
  );
}